import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

/**
 * Floating "Publish to production" button for the JobCam website.
 *
 * Shows ONLY on the staging site (a `staging.*` hostname). It is never rendered
 * on localhost, on production (jobcam.com.au), or on generic *.pages.dev preview
 * URLs — so react-snap's prerender (which runs on localhost) never bakes it in.
 *
 * State:
 *   - in sync (nothing ahead)  → GREEN  "In sync with production"
 *   - changes queued (ahead>0) → ORANGE "Publish to production" (+ count)
 *   - confirming               → RED    "Confirm publish?"
 *
 * Clicking when changes are queued asks to confirm, then calls /api/publish (a
 * Cloudflare Pages Function) which merges `staging` → `main`, triggering the
 * production Cloudflare deploy. All real authorisation is handled by Cloudflare
 * Access in front of the staging subdomain plus server-side checks in the
 * function — hiding the button is only UX.
 */

function isStagingHost(): boolean {
  if (typeof window === "undefined") return false;
  return window.location.hostname.startsWith("staging.");
}

export default function StagingPublishBar() {
  const [visible, setVisible] = useState(false);
  const [ahead, setAhead] = useState<number | null>(null);
  const [latest, setLatest] = useState<string>("");
  const [confirming, setConfirming] = useState(false);
  const [publishing, setPublishing] = useState(false);

  // Resolve visibility on the client only (avoids SSR/prerender mismatch).
  useEffect(() => {
    setVisible(isStagingHost());
  }, []);

  const loadStatus = useCallback(async () => {
    try {
      const r = await fetch("/api/publish", { credentials: "include" });
      const d = await r.json();
      if (d.ok) {
        setAhead(typeof d.ahead === "number" ? d.ahead : 0);
        setLatest(d.latest ?? "");
      }
    } catch {
      /* status is best-effort; leave it blank */
    }
  }, []);

  useEffect(() => {
    if (!visible) return;
    loadStatus();
    const id = setInterval(loadStatus, 30000);
    return () => clearInterval(id);
  }, [visible, loadStatus]);

  // Auto-cancel the confirm prompt if it isn't acted on quickly.
  useEffect(() => {
    if (!confirming) return;
    const t = setTimeout(() => setConfirming(false), 4000);
    return () => clearTimeout(t);
  }, [confirming]);

  const publish = useCallback(async () => {
    setPublishing(true);
    setConfirming(false);
    try {
      const r = await fetch("/api/publish", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      const d = await r.json();
      if (d.ok) toast.success(d.detail || "Published — Cloudflare is building the deploy.");
      else toast.error(d.error || "Publish failed.");
      loadStatus();
    } catch {
      toast.error("Couldn't reach the publish service.");
    } finally {
      setPublishing(false);
    }
  }, [loadStatus]);

  if (!visible) return null;

  const loading = ahead === null;
  const count = ahead ?? 0;
  const inSync = !loading && count === 0;

  const bg = publishing
    ? "#f97316"
    : confirming
      ? "#dc2626" // red — confirm
      : loading
        ? "#64748b" // slate — checking
        : inSync
          ? "#16a34a" // green — in sync
          : "#f97316"; // orange — changes queued

  const label = publishing
    ? "Publishing…"
    : confirming
      ? "Confirm publish?"
      : loading
        ? "Checking…"
        : inSync
          ? "In sync with production"
          : "Publish to production";

  function handleClick() {
    if (publishing || loading) return;
    if (inSync) {
      loadStatus();
      return;
    }
    if (confirming) {
      publish();
      return;
    }
    setConfirming(true);
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={publishing}
      title={latest ? `Latest on staging: ${latest}` : "Promote staging → production"}
      style={{
        position: "fixed",
        right: "16px",
        top: "16px",
        zIndex: 2147483647,
        backgroundColor: bg,
        color: "#ffffff",
        border: "none",
        borderRadius: "9999px",
        padding: "10px 16px",
        fontSize: "14px",
        fontWeight: 600,
        cursor: publishing ? "default" : "pointer",
        boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
        display: "flex",
        alignItems: "center",
        gap: "8px",
      }}
    >
      {label}
      {!publishing && !confirming && count > 0 && (
        <span
          style={{
            backgroundColor: "rgba(255,255,255,0.25)",
            borderRadius: "9999px",
            padding: "0 8px",
            fontSize: "12px",
            lineHeight: "20px",
          }}
        >
          {count}
        </span>
      )}
    </button>
  );
}
